import { createContext, useContext } from 'react';
import { useQuery } from '@apollo/client';

import type { ReactNode } from 'react';
import type { GetProfileMarblesQuery, GetProfileQuery, UserProfile } from 'types/__generated__';

import { UserStatus } from 'types/__generated__';
import { GET_PROFILE } from 'graphql/queries/getProfile';
import { GET_PROFILE_MARBLES } from 'graphql/queries/getProfileMarbles';

const UserProfileContext = createContext<GetProfileQuery['me'] | null>(null);

interface Props {
  children: ReactNode;
}

const mergeStatus = (magnifierStatus: UserStatus | undefined, marblesStatus: UserStatus | undefined) => {
  if (magnifierStatus === UserStatus.Suspended || marblesStatus === UserStatus.Suspended) return UserStatus.Suspended;

  if (magnifierStatus === UserStatus.SuspensionLifted || marblesStatus === UserStatus.SuspensionLifted)
    return UserStatus.SuspensionLifted;

  return UserStatus.Active;
};

export function UserProfileProvider({ children }: Props) {
  const isOvaEnabled = Boolean(window._env.ovaEnabled) || false;
  const magnifierQuery = useQuery<GetProfileQuery>(GET_PROFILE, { skip: !isOvaEnabled });
  const marblesQuery = useQuery<GetProfileMarblesQuery>(GET_PROFILE_MARBLES);

  if (magnifierQuery.loading || marblesQuery.loading) return null;

  const error = magnifierQuery.error || marblesQuery.error;
  if (error) {
    throw error;
  }

  const emptyObject = {};

  const status = mergeStatus(magnifierQuery.data?.me.status, marblesQuery.data?.marblesMe.status);

  const profile = {
    ...(magnifierQuery.data?.me ?? emptyObject),
    ...(marblesQuery.data?.marblesMe ?? emptyObject),
    status: status,
    enabledModules:
      status !== UserStatus.Suspended
        ? [...(magnifierQuery.data?.me.enabledModules ?? []), ...(marblesQuery.data?.marblesMe.enabledModules ?? [])]
        : [],
    allowedUserActions:
      status !== UserStatus.Suspended
        ? [
            ...(magnifierQuery.data?.me.allowedUserActions ?? []),
            ...(marblesQuery.data?.marblesMe.allowedUserActions ?? []),
          ]
        : [],
  } as UserProfile;

  return <UserProfileContext.Provider value={profile}>{children}</UserProfileContext.Provider>;
}

export function useProfile() {
  const context = useContext(UserProfileContext);

  if (context == null) throw new Error('useProfile can only be used in a UserProfileProvider');

  return context;
}
